import React, { useState } from 'react'
import { Menu, X, Bell, Search, User } from 'lucide-react'
import { MobileSidebar } from './MobileSidebar'
import { useP2PStore } from '../../store/p2pStore'
import { useNavigate } from 'react-router-dom'
import { useAvatarPreview } from '../../contexts/AvatarPreviewContext'

export function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const navigate = useNavigate()

  // Get avatar data from P2P store
  const localSelfta = useP2PStore(state => state.localSelfta)
  // Get preview context for real-time updates during editing
  const { avatarPreview } = useAvatarPreview()

  // Use preview data if available (user is editing), otherwise use store data
  const displayData = avatarPreview || localSelfta

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const handleAvatarClick = () => {
    navigate('/avatar')
  }

  // CRITICAL FIX: Remove competing avatar loading from Header
  // Avatar data is now properly loaded during Zustand rehydration
  // This prevents race conditions that were overwriting the profileImage
  React.useEffect(() => {
    console.log('🔄 Header mounted - using avatar data from store (no manual loading)')
  }, [])

  return (
    <>
      <header
        className="bg-white border-b border-secondary-200 shadow-soft sticky top-0 z-30"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
        }}
      >
        <div className="flex items-center justify-between h-16 px-4 sm:px-6">
          {/* Mobile menu button */}
          <div className="flex items-center md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-lg text-secondary-600 hover:bg-secondary-100 active:bg-secondary-200 hover:text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200 touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>

            <div className="ml-4 flex items-center">
              <div className="flex items-center justify-center w-8 h-8 bg-primary-600 rounded-lg shadow-sm">
                <span className="text-white font-bold text-sm">S</span>
              </div>
              <span className="ml-2 text-lg font-semibold text-secondary-900">
                useSelft
              </span>
            </div>
          </div>

          {/* Search bar (desktop) */}
          <div className="hidden md:flex flex-1 max-w-md">
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="h-4 w-4 text-secondary-400" />
              </div>
              <input
                type="text"
                placeholder="Search connections, messages..."
                className="input pl-10 pr-4 w-full"
              />
            </div>
          </div>

          {/* Right side actions */}
          <div className="flex items-center space-x-2">
            {/* Notifications */}
            <button
              className="p-2 rounded-lg text-secondary-600 hover:bg-secondary-100 active:bg-secondary-200 hover:text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200 touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Notifications"
            >
              <Bell className="h-5 w-5" />
            </button>

            {/* User avatar */}
            <button
              onClick={handleAvatarClick}
              className="flex items-center justify-center w-10 h-10 rounded-full overflow-hidden bg-gray-100 hover:ring-2 hover:ring-primary-500 hover:ring-offset-1 active:ring-4 active:ring-primary-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 touch-manipulation"
              title={displayData ? `${displayData.displayName} (@${displayData.alias})` : 'Click to setup your avatar'}
              aria-label={displayData ? `Profile: ${displayData.displayName}` : 'Setup avatar'}
            >
              {displayData?.profileImage ? (
                <div className="w-full h-full flex items-center justify-center">
                  <img
                    src={displayData.profileImage}
                    alt={`${displayData.displayName}'s avatar`}
                    className="w-full h-full object-contain max-w-full max-h-full"
                  />
                </div>
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                  {displayData ? (
                    <span className="text-white text-sm font-medium">
                      {displayData.displayName?.charAt(0).toUpperCase() || displayData.alias?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  ) : (
                    <User className="w-4 h-4 text-white" />
                  )}
                </div>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile sidebar */}
      <MobileSidebar 
        isOpen={isMobileMenuOpen} 
        onClose={() => setIsMobileMenuOpen(false)} 
      />
    </>
  )
}